import React, { useState, useEffect } from 'react';
import { Stack, Loader, Center, Transition, Paper, Group, ActionIcon, Divider} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { ImageDetails } from '../../../../types/Image';
import { ShoeboxLocation } from '../../../../types/Location';
import { EventData } from '../../../../types/Event';
import { DRAWER_WIDTH } from '../index';
import { 
  EventSection,
  LocationSection,
  FilenameSection,
  DatetimeSection,
  ExifDataSection,
  KeywordsSection,
  PeopleSection,
  CaptionSection,
  RatingSection 
} from './drawer-sections';

interface MetadataDrawerProps {
  metadata: ImageDetails;
  event?: EventData; // Optional event data if the image is part of an event
  drawerState: {
    opened: boolean;
    open: () => void;
    close: () => void;
  };
  formState?: any; // Add this to match the props being passed
  saveFileName?: (filename: string) => void; // Add this to match the props being passed
  saveDatetime?: (datetime: string) => void; // Add this to match the props being passed
  onEventUpdate?: (updatedEvent: EventData) => void; // Callback for updating event data
}

export const MetadataDrawer: React.FC<MetadataDrawerProps> = ({
  metadata: initialMetadata,
  drawerState,
  event,
  onEventUpdate,
}) => {
  const [metadata, setMetadata] = useState<ImageDetails>((initialMetadata || {}) as ImageDetails);
  const { opened, close } = drawerState;
  
  // Update metadata when initialMetadata changes
  useEffect(() => {
    if (initialMetadata) {
      setMetadata(initialMetadata);
    }
  }, [initialMetadata]);
  
  // Debug: Log metadata state changes // todo: delete/surpress
  useEffect(() => {
    console.log('Current metadata state:', metadata);
  }, [metadata]);
  
  // No more editing states needed as each section manages its own state

  /**
   * Updates metadata with named optional parameters
   * @param options Object containing metadata fields to update
   */
  const updateMetadata = (options: Partial<ImageDetails>) => {
    setMetadata({
      ...metadata,
      ...options
    });
  };

  return (
    <Transition
      mounted={opened}
      transition="slide-left"
      duration={300}
      timingFunction="ease"
    >
      {(styles) => (
        <Paper
          style={{
            ...styles,
            position: 'fixed',
            top: 0,
            right: 0,
            height: '100vh',
            width: DRAWER_WIDTH,
            zIndex: 9,
            overflow: 'auto',
            padding: '16px'
          }}
          withBorder
        >
          <Group justify="flex-end" mb="md">
            <ActionIcon
              variant="subtle"
              onClick={close}
              size="lg"
            >
              <IconX size={20}/>
            </ActionIcon>
          </Group>
          
          {!metadata || Object.keys(metadata).length === 0 ? (
            <Center p="xl">
              <Loader />
            </Center>
          ) : (
            <Stack gap="lg">
              {/* Event Section */}
              <EventSection 
                event={event}
                onEventUpdate={(updatedEventData) => {
                  console.log('Updating event data:', updatedEventData);
                  if (onEventUpdate && event) {
                    // Ensure we maintain the required fields from the original event
                    onEventUpdate({
                      ...event,
                      ...updatedEventData,
                      // Ensure key is always present
                      key: updatedEventData.key || event.key
                    });
                  }
                }}
              />

              <Divider my="xs" label="Info" labelPosition="left"/>

              {/* Filename Section */}
              <FilenameSection 
                filename={metadata.file?.display_name || ''}
                onSaveFileName={(newFileName: string) => {
                  console.log('Saving new filename:', newFileName);
                  updateMetadata({
                    file: {
                      ...metadata.file,
                      display_name: newFileName
                    }
                  });
                }}
              />
              
              {/* File Date & Time Section */}
              <DatetimeSection 
                datetime={metadata.datetime || null}
                eventDate={event?.date}
                onSave={(isoDateString: string) => {
                  console.log('Saving new datetime:', isoDateString);
                  updateMetadata({ datetime: isoDateString });
                }}
                onSaveEventDate={event ? (newDate: Date) => {
                  console.log('Updating event date:', newDate);
                  // This would be handled by the parent component
                  // that manages event data
                  if (event && onEventUpdate) {
                    onEventUpdate({
                      ...event,
                      date: newDate
                    });
                  }
                } : undefined}
              />
              
              {/* Caption Section */}
              <CaptionSection 
                caption={metadata.description || ''}
                onSave={(caption: string) => {
                  console.log('Saving caption:', caption);
                  updateMetadata({ description: caption });
                }}
              />
              
              <KeywordsSection 
                keywords={metadata.keywords || []}
                eventKeywords={event?.keywords || []}
                onSave={(newKeywords: string[]) => {
                  console.log('Saving keywords:', newKeywords);
                  updateMetadata({ keywords: newKeywords });
                }}
              />

              {/* Rating Section */}
              <RatingSection 
                rating={metadata.rating || 0}
                onSave={(newRating: number) => {
                  console.log('Saving rating:', newRating);
                  updateMetadata({ rating: newRating });
                }}
              />
              
              {/* Location Section */}
              <LocationSection 
                location={metadata.location || undefined}
                eventLocation={event?.location}
                hasEvent={!!event} // Pass flag indicating if image is part of an event
                onSaveLocation={(updatedLocation: ShoeboxLocation) => {
                  console.log('Saving location:', updatedLocation);
                  updateMetadata({ location: updatedLocation });
                }}
                onSaveEventLocation={event ? (updatedLocation: ShoeboxLocation) => {
                  console.log('Updating event location:', updatedLocation);
                  // This would be handled by the parent component
                  // that manages event data
                  if (event && onEventUpdate) {
                    onEventUpdate({
                      ...event,
                      location: updatedLocation
                    });
                  }
                } : undefined}
              />

              <ExifDataSection metadata={metadata} />
            </Stack>
          )}
        </Paper>
      )}
    </Transition>
  );
};
